import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Modal, Spin, Tooltip, message } from "antd";
import { MessageCircleMore, Send, Pencil, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { mixGetV1, mixPostV1, mixPutV1, mixDelV1 } from "services/mixin";
import useAuthStore from "store/authStore";

interface Note {
    id: number;
    name: string;
    text: string;
    time: string;
    date: string;
    /** users.id của người tạo — dùng để chỉ cho sửa/xóa ghi chú của chính mình */
    createdById: number | null;
}

type NoteBoxProps = {
    appointmentId?: number | null;
    permissionModule?: 'appointment' | 'consultation' | 'execution';
};

type NoteApiItem = {
    id: number;
    appointment_id: number;
    staff_id: number;
    created_by_id?: number | null;
    content: string;
    created_at: string;
    created_by?: {
        id?: number;
        fullname?: string;
        name?: string;
    };
    staff?: {
        id: number;
        name?: string;
        fullname?: string;
    };
    creator?: {
        id?: number;
        fullname?: string;
        name?: string;
    };
    _creator?: {
        id?: number;
        fullname?: string;
        name?: string;
    };
};

const resolveCreatorName = (item: NoteApiItem): string => {
    return (
        item.created_by?.fullname ||
        item.created_by?.name ||
        item.creator?.fullname ||
        item.creator?.name ||
        item._creator?.fullname ||
        item._creator?.name ||
        item.staff?.name ||
        item.staff?.fullname ||
        "--"
    );
};

const resolveCreatorId = (item: NoteApiItem): number | null => {
    const raw = item.created_by_id ?? item.created_by?.id ?? item.creator?.id ?? item._creator?.id;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
};

const mapNoteItem = (item: NoteApiItem): Note => ({
    id: item.id,
    name: resolveCreatorName(item),
    text: item.content || "",
    time: dayjs(item.created_at).format("HH:mm"),
    date: dayjs(item.created_at).format("DD/MM/YYYY"),
    createdById: resolveCreatorId(item),
});

/** users.id của tài khoản đang đăng nhập (khác với staff id trong info.id) */
const getCurrentUserId = (): number | null => {
    try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        const id = Number(stored?.user_id);
        return Number.isFinite(id) ? id : null;
    } catch {
        return null;
    }
};

export default function NoteBox({ appointmentId, permissionModule = 'appointment' }: NoteBoxProps) {
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canAddNote = isSuperAdmin || hasPermission(`${permissionModule}.note`);
    const currentUserId = useMemo(() => getCurrentUserId(), []);
    // Chỉ sửa/xóa được ghi chú do chính mình viết; riêng super admin toàn quyền
    const isOwnNote = (note: Note) =>
        currentUserId != null && note.createdById != null && note.createdById === currentUserId;
    const canModifyNote = (note: Note) => isSuperAdmin || isOwnNote(note);
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const [content, setContent] = useState("");

    // Modal sửa ghi chú
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [editContent, setEditContent] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const fetchNotes = useCallback(async () => {
        if (!appointmentId) {
            setNotes([]);
            return;
        }

        setLoading(true);
        try {
            const res = await mixGetV1(`/api/v1/be/appointments/${appointmentId}/notes`);
            if (!res?.status) {
                throw new Error(res?.msg || "Không tải được ghi chú");
            }

            const rawItems = Array.isArray(res?.data) ? res.data : [];
            setNotes(rawItems.map(mapNoteItem));
        } catch (error: any) {
            message.error(error?.message || "Không tải được ghi chú");
        } finally {
            setLoading(false);
        }
    }, [appointmentId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleSend = async () => {
        const trimmedContent = content.trim();
        if (!canAddNote) {
            message.warning("Bạn không có quyền thêm ghi chú");
            return;
        }
        if (!appointmentId || !trimmedContent || sending) return;

        setSending(true);
        try {
            const res = await mixPostV1(`/api/v1/be/appointments/${appointmentId}/notes`, {
                content: trimmedContent,
            });

            if (!res?.status || !res?.data) {
                throw new Error(res?.msg || "Thêm ghi chú thất bại");
            }

            setNotes((prev) => [...prev, mapNoteItem(res.data)]);
            setContent("");
            message.success(res?.msg || "Thêm ghi chú thành công");
        } catch (error: any) {
            message.error(error?.message || "Thêm ghi chú thất bại");
        } finally {
            setSending(false);
        }
    };

    const openEdit = (note: Note) => {
        setEditingNote(note);
        setEditContent(note.text);
    };

    const closeEdit = () => {
        setEditingNote(null);
        setEditContent("");
    };

    const handleUpdate = async () => {
        const trimmed = editContent.trim();
        if (!appointmentId || !editingNote || !trimmed || savingEdit) return;
        if (!canModifyNote(editingNote)) {
            message.warning("Chỉ sửa được ghi chú do bạn viết");
            return;
        }

        setSavingEdit(true);
        try {
            const res = await mixPutV1(
                `/api/v1/be/appointments/${appointmentId}/notes/${editingNote.id}`,
                { content: trimmed }
            );

            if (!res?.status || !res?.data) {
                throw new Error(res?.msg || "Cập nhật ghi chú thất bại");
            }

            const updated = mapNoteItem(res.data);
            setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
            message.success(res?.msg || "Cập nhật ghi chú thành công");
            closeEdit();
        } catch (error: any) {
            message.error(error?.message || "Cập nhật ghi chú thất bại");
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDelete = (note: Note) => {
        if (!appointmentId) return;
        if (!canModifyNote(note)) {
            message.warning("Chỉ xóa được ghi chú do bạn viết");
            return;
        }
        Modal.confirm({
            title: "Xóa ghi chú?",
            content: "Ghi chú đã xóa sẽ không khôi phục lại được.",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                setDeletingId(note.id);
                try {
                    const res = await mixDelV1(
                        `/api/v1/be/appointments/${appointmentId}/notes/${note.id}`,
                        {}
                    );
                    if (!res?.status) {
                        throw new Error(res?.msg || "Xóa ghi chú thất bại");
                    }
                    setNotes((prev) => prev.filter((n) => n.id !== note.id));
                    message.success(res?.msg || "Xóa ghi chú thành công");
                } catch (error: any) {
                    message.error(error?.message || "Xóa ghi chú thất bại");
                    throw error; // giữ modal mở khi lỗi
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    const groupByDate = useMemo(() => notes.reduce((acc: any, note) => {
        if (!acc[note.date]) acc[note.date] = [];
        acc[note.date].push(note);
        return acc;
    }, {}), [notes]);

    return (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 text-000000e0">
            <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                <MessageCircleMore
                    size={16}
                    className="text-[#4e91ab]"
                />
                Ghi chú lịch hẹn
            </div>
            {/* LIST */}
            <div className="list-box-note overflow-y-auto p-4 space-y-4 bg-white dark:bg-[#1f1f1f]">
                {loading ? (
                    <div className="flex h-full items-center justify-center">
                        <Spin />
                    </div>
                ) : !appointmentId ? (
                    <div className="flex h-full items-center justify-center">
                        <Empty description="Chưa có lịch hẹn" />
                    </div>
                ) : notes.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                        <Empty description="Chưa có ghi chú" />
                    </div>
                ) : Object.keys(groupByDate).map((date) => (
                    <div key={date}>
                        {/* DATE */}
                        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {date}
                        </div>

                        {/* NOTES */}
                        <div className="space-y-3">
                            {groupByDate[date].map((n: Note) => (
                                <div key={n.id} className="flex gap-2 text-000000e0 group">
                                    {/* Avatar */}
                                    <div className="w-6 h-6 min-w-[24px] min-h-[24px] text-[12px] rounded-full flex items-center justify-center text-white bg-purple-500 flex-shrink-0">
                                        {n.name[0]}
                                    </div>

                                    {/* Content */}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium flex items-center">
                                            {n.name}
                                            <span className="ml-2 text-xs text-gray-400">
                                                {n.time}
                                            </span>

                                            {/* Thao tác: chỉ hiện khi rê chuột để không rối danh sách */}
                                            {canAddNote && canModifyNote(n) && (
                                                <span className="ml-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                                                    <Tooltip title="Sửa ghi chú">
                                                        <button
                                                            onClick={() => openEdit(n)}
                                                            className="text-gray-400 hover:text-[#3e91ab] transition"
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip title="Xóa ghi chú">
                                                        <button
                                                            onClick={() => handleDelete(n)}
                                                            disabled={deletingId === n.id}
                                                            className="text-gray-400 hover:text-red-500 transition disabled:opacity-50"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </Tooltip>
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                                            {n.text}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* INPUT */}
            <div className="py-3 border-t bg-white dark:bg-[#1f1f1f] dark:border-gray-700">
                <div className="border rounded-xl p-3 relative dark:border-gray-700">

                    {/* TEXTAREA */}
                    <Input.TextArea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={
                            !canAddNote
                                ? "Bạn không có quyền thêm ghi chú"
                                : sending
                                    ? "Đang gửi ghi chú..."
                                    : "Nhập Ghi chú"
                        }
                        autoSize={{ minRows: 3, maxRows: 3 }}
                        disabled={!appointmentId || sending || !canAddNote}
                        className="!border-none !shadow-none px-0 pt-0 pb-2 !resize-none"
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />

                    {/* ACTION */}
                    <div className="bottom-3 flex items-center justify-end gap-2">
                        {sending && (
                            <span className="text-xs text-gray-500">Đang gửi...</span>
                        )}
                        {/* Send */}
                        <button
                            onClick={handleSend}
                            disabled={!appointmentId || sending || !content.trim() || !canAddNote}
                            className="bg-[#3e91ab] hover:bg-[#357a91] disabled:bg-[#9fc5d1] text-white w-8 h-8 rounded-full flex items-center justify-center transition"
                        >
                            {sending ? <Spin size="small" /> : <Send size={16} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* MODAL SỬA GHI CHÚ */}
            <Modal
                open={!!editingNote}
                title="Sửa ghi chú"
                onCancel={closeEdit}
                maskClosable={!savingEdit}
                footer={[
                    <Button key="cancel" onClick={closeEdit} disabled={savingEdit}>
                        Hủy
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={savingEdit}
                        disabled={!editContent.trim()}
                        onClick={handleUpdate}
                    >
                        Lưu
                    </Button>,
                ]}
            >
                {/* pb-6: antd đặt bộ đếm ký tự lơ lửng dưới ô nhập, không chừa chỗ thì
                    nó đè lên hàng nút Hủy/Lưu của modal */}
                <div className="pb-6">
                    <Input.TextArea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        placeholder="Nhập nội dung ghi chú"
                        autoSize={{ minRows: 5, maxRows: 12 }}
                        maxLength={5000}
                        showCount
                    />
                </div>
            </Modal>
        </div>
    );
}